var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import classNames from 'classnames';
import { ThemeProvider, useTheme } from "../../hooks/useTheme";
import { makeStyles } from "../../styles/index";
import Check from "../../icons/Check";
import CheckOutline from "../../icons/CheckOutline";
// defaultThemeに無い色
var LIGHT_UNCHECKED_HOVER_TEXT_COLOR = '#b9c4db';
var DARK_UNCHECKED_HOVER_TEXT_COLOR = '#727a8a';
export var styles = function (theme) { return ({
    root: {
        display: 'flex',
        flex: '0 1 auto',
        position: 'relative',
        cursor: 'pointer',
        alignItems: 'center',
        userSelect: 'none',
    },
    border: {
        height: 30,
        width: 30,
        color: '#fff',
        cursor: 'inherit',
        position: 'relative',
        borderWidth: theme.shape.borderWidth.normal,
        borderStyle: 'solid',
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: '0.3s',
    },
    icon: {
        flex: '1 0 auto',
        pointerEvents: 'none',
    },
    input: {
        cursor: 'inherit',
        position: 'absolute',
        opacity: 0,
        height: '100%',
        width: '100%',
        left: 0,
        top: 0,
        margin: 0,
        userSelect: 'none',
    },
    label: {
        cursor: 'inherit',
        userSelect: 'none',
        marginLeft: theme.spacing(1),
    },
    disabled: {
        cursor: 'auto',
        pointerEvents: 'none',
    },
    checked: {},
    light: {
        // unchecked normal
        '& $border': {
            color: theme.palette.light.text.disabled,
            borderColor: theme.palette.light.text.primary,
        },
        '& $label': {
            color: theme.palette.light.text.primary,
        },
        // unchecked disabled
        '&$disabled': {
            '& $border': {
                color: 'transparent',
                borderColor: theme.palette.light.action.disabled,
            },
            '& $label': {
                color: theme.palette.light.action.disabled,
            },
        },
        // unchecked hover
        '&:hover': {
            '& $border': {
                color: LIGHT_UNCHECKED_HOVER_TEXT_COLOR,
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary[50],
            },
        },
        // unchecked active
        '&:hover:active': {
            '& $border': {
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.dark,
            },
        },
        // checked
        '&$checked': {
            // checked normal
            '& $border': {
                color: theme.palette.dark.text.primary,
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
            },
            // checked disabled
            '&$disabled': {
                '& $border': {
                    color: theme.palette.dark.text.primary,
                    borderColor: theme.palette.light.action.disabled,
                    backgroundColor: theme.palette.light.action.disabled,
                },
            },
            // checked hover
            '&:hover': {
                '& $border': {
                    color: theme.palette.dark.text.primary,
                    backgroundColor: theme.palette.primary[200],
                },
            },
            // checked active
            '&:hover:active': {
                '& $border': {
                    color: theme.palette.dark.text.primary,
                    backgroundColor: theme.palette.primary.dark,
                },
            },
        },
    },
    dark: {
        // unchecked normal
        '& $border': {
            color: theme.palette.dark.text.disabled,
            borderColor: theme.palette.dark.text.primary,
        },
        '& $label': {
            color: theme.palette.dark.text.primary,
        },
        // unchecked disabled
        '&$disabled': {
            '& $border': {
                borderColor: theme.palette.dark.action.disabled,
            },
            '& $label': {
                color: theme.palette.dark.action.disabled,
            },
        },
        // unchecked hover
        '&:hover': {
            '& $border': {
                color: DARK_UNCHECKED_HOVER_TEXT_COLOR,
                borderColor: theme.palette.primary[100],
            },
        },
        // unchecked active
        '&:hover:active': {
            '& $border': {
                color: theme.palette.primary[300],
                borderColor: theme.palette.primary[100],
                backgroundColor: theme.palette.primary[600],
            },
        },
        // checked
        '&$checked': {
            // checked normal
            '& $border': {
                color: theme.palette.dark.text.primary,
                borderColor: theme.palette.primary.light,
                backgroundColor: theme.palette.primary.light,
            },
            // checked disabled
            '&$disabled': {
                '& $border': {
                    borderColor: theme.palette.dark.action.disabled,
                    backgroundColor: theme.palette.dark.action.disabled,
                },
            },
            // checked hover
            '&:hover': {
                '& $border': {
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary[200],
                },
            },
            // checked active
            '&:hover:active': {
                '& $border': {
                    color: theme.palette.dark.text.primary,
                    borderColor: theme.palette.primary[100],
                    backgroundColor: theme.palette.primary[600],
                },
            },
        },
    },
}); };
var useStyles = makeStyles(styles, { name: 'SnCheckbox' });
export var Checkbox = React.forwardRef(function (props, ref) {
    var _a;
    var dark = props.dark, light = props.light, value = props.value, _b = props.defaultValue, defaultValue = _b === void 0 ? false : _b, label = props.label, disabled = props.disabled, onChange = props.onChange, onClick = props.onClick, others = __rest(props, ["dark", "light", "value", "defaultValue", "label", "disabled", "onChange", "onClick"]);
    var classes = useStyles(props);
    var theme = useTheme({ dark: dark, light: light });
    var _c = useState(defaultValue), selectedValue = _c[0], setSelectedValue = _c[1];
    var displayValue = value !== null && value !== void 0 ? value : selectedValue;
    var className = classNames(classes.root, classes[theme.type], (_a = {},
        _a[classes.disabled] = disabled,
        _a[classes.checked] = displayValue,
        _a));
    var handleClick = function (e) {
        if (disabled) {
            return;
        }
        setSelectedValue(!displayValue);
        onChange === null || onChange === void 0 ? void 0 : onChange(!displayValue);
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    var Icon = displayValue ? Check : CheckOutline;
    return (React.createElement(ThemeProvider, { theme: theme },
        React.createElement("div", { className: className, onClick: handleClick },
            React.createElement("div", { className: classes.border },
                React.createElement(Icon, { className: classes.icon }),
                React.createElement("input", __assign({ ref: ref, className: classes.input, role: "checkbox", type: "checkbox", checked: displayValue, readOnly // 外側のonClickで値の制御をするためreadOnlyにしておく
                    : true }, others))),
            label && React.createElement("label", { className: classes.label }, label))));
});
